import * as THREE from 'three';

export interface NpcMeshBundle {
  group: THREE.Group;
  materials: THREE.MeshStandardMaterial[];
  labelOffset: number;
}

export function createNpcMesh(npcType: string, width = 1, height = 1): NpcMeshBundle {
  const group = new THREE.Group();
  const materials: THREE.MeshStandardMaterial[] = [];
  const footprintX = Math.max(0.8, width * 0.9);
  const footprintZ = Math.max(0.8, height * 0.9);

  switch (npcType) {
    case 'chest': {
      const base = new THREE.Mesh(
        new THREE.BoxGeometry(0.55, 0.28, 0.42),
        makeMaterial('#b45309', materials),
      );
      base.position.y = 0.14;
      group.add(base);
      const lid = new THREE.Mesh(
        new THREE.BoxGeometry(0.56, 0.12, 0.44),
        makeMaterial('#fbbf24', materials, { emissive: '#ca8a04', emissiveIntensity: 0.18 }),
      );
      lid.position.set(0, 0.34, -0.02);
      lid.rotation.x = -0.28;
      group.add(lid);
      return { group, materials, labelOffset: 0.8 };
    }
    case 'shrine': {
      const base = new THREE.Mesh(
        new THREE.BoxGeometry(0.8, 0.18, 0.8),
        makeMaterial('#cbd5e1', materials),
      );
      base.position.y = 0.09;
      group.add(base);
      const roof = new THREE.Mesh(
        new THREE.ConeGeometry(0.56, 0.45, 4),
        makeMaterial('#9f1239', materials),
      );
      roof.position.y = 0.54;
      roof.rotation.y = Math.PI / 4;
      group.add(roof);
      return { group, materials, labelOffset: 1.05 };
    }
    case 'fountain': {
      const basin = new THREE.Mesh(
        new THREE.CylinderGeometry(0.42, 0.48, 0.18, 16),
        makeMaterial('#94a3b8', materials),
      );
      basin.position.y = 0.09;
      group.add(basin);
      const water = new THREE.Mesh(
        new THREE.CylinderGeometry(0.34, 0.34, 0.08, 16),
        makeMaterial('#38bdf8', materials, { emissive: '#0ea5e9', emissiveIntensity: 0.3 }),
      );
      water.position.y = 0.16;
      group.add(water);
      const column = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.09, 0.46, 12),
        makeMaterial('#cbd5e1', materials),
      );
      column.position.y = 0.36;
      group.add(column);
      return { group, materials, labelOffset: 0.95 };
    }
    case 'market_stall': {
      const roof = new THREE.Mesh(
        new THREE.ConeGeometry(0.55, 0.36, 4),
        makeMaterial('#ea580c', materials),
      );
      roof.position.y = 0.72;
      roof.rotation.y = Math.PI / 4;
      group.add(roof);
      const counter = new THREE.Mesh(
        new THREE.BoxGeometry(0.86, 0.32, 0.54),
        makeMaterial('#7c2d12', materials),
      );
      counter.position.y = 0.24;
      group.add(counter);
      return { group, materials, labelOffset: 1.15 };
    }
    case 'town_wall_h':
    case 'town_wall_v':
    case 'town_gate': {
      const wall = new THREE.Mesh(
        new THREE.BoxGeometry(footprintX, npcType === 'town_gate' ? 0.9 : 1.15, footprintZ),
        makeMaterial('#64748b', materials),
      );
      wall.position.y = npcType === 'town_gate' ? 0.45 : 0.58;
      group.add(wall);
      if (npcType === 'town_gate') {
        const arch = new THREE.Mesh(
          new THREE.BoxGeometry(footprintX * 0.9, 0.18, footprintZ * 0.9),
          makeMaterial('#94a3b8', materials),
        );
        arch.position.y = 0.9;
        group.add(arch);
      }
      return { group, materials, labelOffset: 1.3 };
    }
    case 'quest_giver':
    default: {
      const body = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.18, 0.35, 4, 10),
        makeMaterial('#eab308', materials, { emissive: '#f59e0b', emissiveIntensity: 0.16 }),
      );
      body.position.y = 0.44;
      group.add(body);
      const marker = new THREE.Mesh(
        new THREE.TorusGeometry(0.12, 0.03, 8, 16),
        makeMaterial('#f8fafc', materials, { emissive: '#ffffff', emissiveIntensity: 0.6 }),
      );
      marker.position.set(0, 0.94, 0);
      marker.rotation.x = Math.PI / 2;
      group.add(marker);
      return { group, materials, labelOffset: 1.2 };
    }
  }
}

function makeMaterial(
  color: string,
  materials: THREE.MeshStandardMaterial[],
  overrides?: Partial<THREE.MeshStandardMaterialParameters>,
): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.64,
    metalness: 0.1,
    transparent: true,
    ...overrides,
  });
  materials.push(material);
  return material;
}
